import React, { useCallback } from 'react';
import { useOnboardingChecklist } from '../../hooks/onboarding';
import { useStripeStatus } from '@/hooks/useStripeStatus';
import { AlertCircle, CreditCard, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useStripeConnection } from '@/hooks/useStripeConnection';

interface OnboardingBlockerProps {
  children: React.ReactNode;
  requireStripe?: boolean;
  requireBusinessProfile?: boolean;
  customMessage?: string;
}

/**
 * Component that blocks access to dashboard features during onboarding
 * Shows an overlay with guidance when requirements are not met
 */
const OnboardingBlocker: React.FC<OnboardingBlockerProps> = ({ 
  children, 
  requireStripe = false,
  requireBusinessProfile = false,
  customMessage
}) => {
  const { stripeConnected, businessProfileCompleted, isLoading } = useOnboardingChecklist();
  const { handleConnect, isConnecting } = useStripeConnection();
  const navigate = useNavigate();
  const navigateToProfile = useCallback(() => {
    navigate('/settings?tab=profile');
  }, [navigate]);

  if (isLoading) {
    return <>{children}</>;
  }

  const shouldBlock = (requireStripe && !stripeConnected) || 
                     (requireBusinessProfile && !businessProfileCompleted);

  if (!shouldBlock) {
    return <>{children}</>;
  }

  const missingStripe = requireStripe && !stripeConnected;
  const missingProfile = requireBusinessProfile && !businessProfileCompleted;

  return (
    <div className="relative min-h-[400px]">
      <div className="blur-sm pointer-events-none select-none opacity-60">
        {children}
      </div>
      <div className="absolute inset-0 bg-white/95 flex items-center justify-center rounded-lg border-2 border-gray-300 min-h-[400px]">
        <div className="text-center max-w-md p-8 w-full">
          <div className="mb-6">
            <AlertCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Setup Required
            </h3>
            <p className="text-base text-gray-600 mb-6 leading-relaxed">
              {customMessage || 
                (missingStripe && missingProfile 
                  ? "Connect your Stripe account and complete your business profile to access this feature."
                  : missingStripe 
                    ? "Connect your Stripe account to access payment features."
                    : "Complete your business profile to access this feature."
                )
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingBlocker;
